// Utils
import classnames from 'classnames'
import { FC, SVGProps } from 'react'

import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'

import StarIcon from '../../../assets/star.inline.svg'
import Icon from '../../icon'

import styles from './reviewStars.module.scss'

interface ReviewStarsProps {
  totalStars: number
  fillStars: number
  className?: string
  classNameIcon?: string
  handleClick?: (star: number) => void
  starSvg?: ((any: any) => JSX.Element) | FC<SVGProps<SVGElement>>
  emptyIconClass?: string
  fillIconClass?: string
}

function getFillPercentage(rating: number, index: number, isPartialReviewStarsEnabled: boolean): number {
  if (!isPartialReviewStarsEnabled) {
    return index + 1 <= Math.round(rating) ? 100 : 0
  }

  const fraction = Math.max(0, Math.round((rating - index) * 10) / 10) // using Math.round to fix floating-point precision issue
  if (fraction >= 0.8) return 100
  if (fraction >= 0.4) return 50
  return 0
}

export const ReviewStars = ({ className = '', ...props }: ReviewStarsProps) => {
  const rating = Number(getFormattedAverageRating(props.fillStars))
  const featureGates = getUserAgnosticGlobalFeatureGatesFromMemory()
  const isPartialReviewStarsEnabled = featureGates?.ssr_partial_review_stars
  const Star = props.starSvg ?? StarIcon

  const baseClass = classnames(styles.starIcon, props.classNameIcon)

  const emptyClass = classnames(props.emptyIconClass ?? styles.ratingStar)
  const filledClass = classnames(props.fillIconClass ?? styles.fillStar)

  return (
    <div className={classnames(styles.reviewStarsWrap, className)} data-testid="review-stars-wrap">
      {Array.from({ length: props.totalStars }, (_, i) => {
        const fillPct = getFillPercentage(rating, i, isPartialReviewStarsEnabled)

        return (
          <span key={i} className={baseClass} onClick={() => props.handleClick?.(i)}>
            <span className={styles.starLayer}>
              <Icon iconSvg={Star} className={emptyClass} />
            </span>
            <span className={styles.starLayer} style={{ clipPath: `inset(0 ${100 - fillPct}% 0 0)` }}>
              <Icon iconSvg={Star} className={filledClass} />
            </span>
          </span>
        )
      })}
    </div>
  )
}

export default ReviewStars
